/*
 * Fixture candidate-plan reviews, keyed by run id — the payload the live
 * verifier serves from /v1/runs/:id/candidate. The candidate is a full
 * ActionPlan (validated against the shared schema by src/data.test.ts); the
 * diff is the explainable comparison against the committed plan.
 *
 * The verifier's diff arrives as `unknown` (api.ts deliberately does not
 * pin its shape); `PlanDiff` here is the dashboard's display contract, and
 * live responses are validated against it with `safeParse` — anything that
 * does not fit is shown as raw JSON, never silently reshaped.
 */
import type { ActionPlan } from '@blume/web-test-model'
import { z } from 'zod'

export const PlanDiffEntry = z.strictObject({
  kind: z.enum(['added', 'removed', 'changed', 'unchanged']),
  stepId: z.string().min(1).max(200),
  description: z.string().min(1).max(500),
  /** Human-readable locator/action before the change. */
  before: z.string().max(2_000).optional(),
  /** Human-readable locator/action the candidate proposes. */
  after: z.string().max(2_000).optional(),
  /** Why the candidate makes this change — the explainable part. */
  reason: z.string().max(2_000).optional()
})
export type PlanDiffEntry = z.infer<typeof PlanDiffEntry>

export const PlanDiff = z.strictObject({
  baselinePlanHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  entries: z.array(PlanDiffEntry).max(1_000)
})
export type PlanDiff = z.infer<typeof PlanDiff>

export interface CandidateReviewFixture {
  candidate: ActionPlan
  diff: PlanDiff
}

const hash = '9'.repeat(64)
const previousHash = '3'.repeat(64)

const assumptions = {
  viewport: { width: 1280, height: 800 },
  locale: 'en-US',
  timezone: 'UTC'
} as const

/** The candidate drafted mid-flight by run-mcp-stabilizing (new scenario). */
const defaultProjectCandidate: ActionPlan = {
  schemaVersion: 1,
  scenarioId: 'navigation.default-project',
  scenarioHash: hash,
  compilerVersion: 'plan-compiler/0.4.2',
  executorProtocolVersion: 1,
  createdAt: '2026-07-29T09:04:41.000Z',
  createdBy: 'ai-exploration',
  target: 'web-lab',
  assumptions,
  steps: [
    {
      id: 'default-1-open-switcher',
      scenarioStepId: 'open-project-switcher',
      description: 'Open the project switcher',
      action: {
        kind: 'click',
        target: {
          primary: { by: 'role', role: 'button', name: 'Current project: Blume' },
          fallbacks: [{ by: 'test-id', value: 'project-switcher-trigger' }],
          rationale:
            'The trigger keeps a stable accessible name; the test id is a fallback in case the current project name changes.'
        }
      },
      assertions: [
        {
          source: { kind: 'visible', target: { by: 'role', role: 'option', name: 'Atlas' } },
          target: {
            primary: { by: 'role', role: 'option', name: 'Atlas' },
            fallbacks: [],
            rationale: 'The Atlas option is uniquely named inside the open listbox.'
          }
        }
      ],
      timeoutMs: 5_000
    },
    {
      id: 'default-2-open-settings',
      scenarioStepId: 'open-project-settings',
      description: 'Open the Atlas project settings',
      action: {
        kind: 'click',
        target: {
          primary: { by: 'role', role: 'menuitem', name: 'Project settings' },
          fallbacks: [],
          rationale: 'The menu item is the only element with this role and name in the switcher.'
        }
      },
      assertions: [],
      timeoutMs: 5_000
    },
    {
      id: 'default-3-set-default',
      scenarioStepId: 'set-default-project',
      description: 'Make Atlas the default project',
      action: {
        kind: 'check',
        target: {
          primary: { by: 'role', role: 'checkbox', name: 'Make Atlas my default project' },
          fallbacks: [{ by: 'label', value: 'Make Atlas my default project' }],
          rationale:
            'The checkbox exposes a stable accessible name; the label fallback survives a role refactor.'
        }
      },
      assertions: [
        {
          source: {
            kind: 'checked',
            target: { by: 'role', role: 'checkbox', name: 'Make Atlas my default project' },
            value: true
          }
        }
      ],
      timeoutMs: 5_000
    },
    {
      id: 'default-4-confirm',
      scenarioStepId: 'confirm-default-project',
      description: 'The confirmation toast appears',
      assertions: [
        {
          source: {
            kind: 'visible',
            target: { by: 'text', value: 'Atlas is now your default project' }
          }
        }
      ],
      timeoutMs: 8_000
    }
  ]
}

const defaultProjectDiff: PlanDiff = {
  entries: [
    {
      kind: 'added',
      stepId: 'default-1-open-switcher',
      description: 'Open the project switcher',
      after: 'click role=button “Current project: Blume”',
      reason: 'This scenario has no committed plan yet — every step is new.'
    },
    {
      kind: 'added',
      stepId: 'default-2-open-settings',
      description: 'Open the Atlas project settings',
      after: 'click role=menuitem “Project settings”'
    },
    {
      kind: 'added',
      stepId: 'default-3-set-default',
      description: 'Make Atlas the default project',
      after: 'check role=checkbox “Make Atlas my default project”'
    },
    {
      kind: 'added',
      stepId: 'default-4-confirm',
      description: 'The confirmation toast appears',
      after: 'assert visible text “Atlas is now your default project”'
    }
  ]
}

/** The repaired locator plan waiting on run-needs-review. */
const weeklySummaryCandidate: ActionPlan = {
  schemaVersion: 1,
  scenarioId: 'usage.weekly-summary',
  scenarioHash: hash,
  compilerVersion: 'plan-compiler/0.4.2',
  executorProtocolVersion: 1,
  createdAt: '2026-07-29T07:15:38.000Z',
  createdBy: 'ai-exploration',
  target: 'web-lab',
  assumptions,
  steps: [
    {
      id: 'weekly-1-open-usage',
      scenarioStepId: 'open-usage',
      description: 'Open the usage page',
      action: { kind: 'navigate', route: '/usage' },
      assertions: [],
      timeoutMs: 10_000
    },
    {
      id: 'weekly-2-open-summary',
      scenarioStepId: 'open-weekly-summary',
      description: 'Open the weekly summary',
      action: {
        kind: 'click',
        target: {
          primary: { by: 'role', role: 'tab', name: 'Weekly summary' },
          fallbacks: [{ by: 'text', value: 'Weekly summary' }],
          rationale:
            'The tab lost its data-testid in the usage redesign; its accessible role and name are stable across both layouts.'
        }
      },
      assertions: [],
      timeoutMs: 5_000
    },
    {
      id: 'weekly-3-verify-chart',
      scenarioStepId: 'verify-summary-chart',
      description: 'The summary chart renders for the current week',
      assertions: [
        {
          source: {
            kind: 'visible',
            target: { by: 'role', role: 'img', name: 'Weekly usage chart' }
          }
        }
      ],
      timeoutMs: 8_000
    }
  ]
}

const weeklySummaryDiff: PlanDiff = {
  baselinePlanHash: previousHash,
  entries: [
    {
      kind: 'unchanged',
      stepId: 'weekly-1-open-usage',
      description: 'Open the usage page',
      after: 'navigate /usage'
    },
    {
      kind: 'changed',
      stepId: 'weekly-2-open-summary',
      description: 'Open the weekly summary',
      before: 'click css [data-testid="usage-weekly-tab"]',
      after: 'click role=tab “Weekly summary”',
      reason:
        'The usage redesign removed the data-testid this step relied on. The accessible role and name exist in both the old and new layout.'
    },
    {
      kind: 'unchanged',
      stepId: 'weekly-3-verify-chart',
      description: 'The summary chart renders for the current week',
      after: 'assert visible role=img “Weekly usage chart”'
    }
  ]
}

/** The repair candidate produced by run-repair-review. */
const exportCsvCandidate: ActionPlan = {
  schemaVersion: 1,
  scenarioId: 'usage.export-csv',
  scenarioHash: hash,
  compilerVersion: 'plan-compiler/0.4.2',
  executorProtocolVersion: 1,
  createdAt: '2026-07-29T09:26:12.000Z',
  createdBy: 'ai-exploration',
  target: 'web-lab',
  assumptions,
  steps: [
    {
      id: 'export-1-open-usage',
      scenarioStepId: 'open-usage',
      description: 'Open the usage page',
      action: { kind: 'navigate', route: '/usage' },
      assertions: [],
      timeoutMs: 10_000
    },
    {
      id: 'export-2-open-export-menu',
      scenarioStepId: 'open-export-menu',
      description: 'Open the export menu',
      action: {
        kind: 'click',
        target: {
          primary: { by: 'role', role: 'button', name: 'Export' },
          fallbacks: [],
          rationale: 'The export trigger kept its accessible name after the toolbar refactor.'
        }
      },
      assertions: [],
      timeoutMs: 5_000
    },
    {
      id: 'export-3-choose-csv',
      scenarioStepId: 'choose-csv',
      description: 'Choose CSV export',
      action: {
        kind: 'click',
        target: {
          primary: { by: 'role', role: 'menuitem', name: 'Download as CSV' },
          fallbacks: [{ by: 'text', value: 'Download as CSV' }],
          rationale:
            'The menu item was renamed from “Export CSV”; the repaired locator matches the new accessible name.'
        }
      },
      assertions: [
        {
          source: {
            kind: 'visible',
            target: { by: 'text', value: 'Preparing your CSV export' }
          }
        }
      ],
      timeoutMs: 8_000
    }
  ]
}

const exportCsvDiff: PlanDiff = {
  baselinePlanHash: previousHash,
  entries: [
    {
      kind: 'unchanged',
      stepId: 'export-1-open-usage',
      description: 'Open the usage page',
      after: 'navigate /usage'
    },
    {
      kind: 'unchanged',
      stepId: 'export-2-open-export-menu',
      description: 'Open the export menu',
      after: 'click role=button “Export”'
    },
    {
      kind: 'changed',
      stepId: 'export-3-choose-csv',
      description: 'Choose CSV export',
      before: 'click role=menuitem “Export CSV”',
      after: 'click role=menuitem “Download as CSV”',
      reason:
        'PR #478 renamed the export menu item. Two repair validations passed with the new name; the product behaviour is unchanged.'
    },
    {
      kind: 'removed',
      stepId: 'export-4-legacy-confirm',
      description: 'Dismiss the legacy export confirmation dialog',
      before: 'click css .export-confirm-dialog button.primary',
      reason: 'The confirmation dialog no longer exists — the export starts immediately.'
    }
  ]
}

export const fixtureCandidateReviews: Record<string, CandidateReviewFixture> = {
  'run-mcp-stabilizing': { candidate: defaultProjectCandidate, diff: defaultProjectDiff },
  'run-needs-review': { candidate: weeklySummaryCandidate, diff: weeklySummaryDiff },
  'run-repair-review': { candidate: exportCsvCandidate, diff: exportCsvDiff }
}
